import { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged, signOut } from '../services/authService';
import { isUserAllowed, isAdmin as checkIsAdmin } from '../services/adminService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAllowed, setIsAllowed] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser?.email) {
        // Catch per-check so a failure in one (e.g. Firestore permissions) doesn't zero out the other
        const [allowed, admin] = await Promise.all([
          isUserAllowed(firebaseUser.email).catch((err) => {
            console.error('isUserAllowed failed:', err);
            return false;
          }),
          checkIsAdmin(firebaseUser.email).catch((err) => {
            console.error('isAdmin failed:', err);
            return false;
          }),
        ]);
        setIsAllowed(allowed);
        setIsAdminUser(admin);
      } else {
        setIsAllowed(false);
        setIsAdminUser(false);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const logout = async () => {
    await signOut();
    setUser(null);
    setIsAllowed(false);
    setIsAdminUser(false);
  };

  // Refresh permissions (e.g. after admin adds user)
  const refreshPermissions = async () => {
    if (user?.email) {
      const [allowed, admin] = await Promise.all([
        isUserAllowed(user.email).catch(() => false),
        checkIsAdmin(user.email).catch(() => false),
      ]);
      setIsAllowed(allowed);
      setIsAdminUser(admin);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, isAllowed, isAdmin: isAdminUser, logout, refreshPermissions }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
